import { AreaState } from './area-state';
import { ConnectionStorage } from './connection-storage';
import { BreakerBoard } from './breaker-boards';
import { IdStorage } from './id-storage';
import { mergeTwoLengthCounts } from '../helpers';

export class EditState {
  constructor(options = {}) {
    ({ selectedLayouts: this.selectedLayouts = [] } = options);
    if (!options.rawAreas) {
      throw Error('Needs to supply raw areas data');
    }

    this.areas = [];
    this.connectionStorage = new ConnectionStorage({ editState: this });
    this.idStorage = new IdStorage();
    this.displayedParallelLayoutOptions = options.displayedParallelLayoutOptions;
    this.edgeLabelingOptions = options.edgeLabelingOptions ?? [];
    this.edgeLabelingMapping = options.edgeLabelingMapping ?? [];

    let createdFromDescriptors = false;
    for (const rawArea of options.rawAreas) {
      const availableBoards = options.availableBoards ? options.availableBoards : [];
      if (rawArea.data.descriptor) {
        this.createAreaStateFromDescriptors(rawArea, availableBoards);
        createdFromDescriptors = true;
      } else {
        const area = new AreaState({
          vertices: rawArea.tessellation.poly.points,
          rawArea: rawArea,
          connectionStorage: this.connectionStorage,
          id: this.idStorage.createId(),
          availableBoards: availableBoards,
          displayedParallelLayoutOptions: this.displayedParallelLayoutOptions,
          edgeLabelingOptions: this.edgeLabelingOptions,
          edgeLabelingMapping: this.edgeLabelingMapping,
        });
        this.areas.push(area);
      }
    }

    if (createdFromDescriptors) {
      this.createConnectionsFromDescriptors(options.rawAreas);
      this.createBreakerBoardsFromDescriptors(options.rawAreas);
    }

    if (this.selectedLayouts) {
      for (const layout of this.selectedLayouts) {
        const area = this.findAreaById(layout.id);
        if (area) {
          area.updateRawAreaData('current_plan_index', parseInt(layout.index));
        }
      }
    }
  }

  createAreaStateFromDescriptors(rawArea, availableBoards) {
    const descriptor = rawArea.data.descriptor;
    const id = descriptor.id;

    this.idStorage.usedIds.push(id);
    const gid = descriptor.group_id;
    const vertices = rawArea.data.points;

    if (descriptor.type !== 'breaker_board') {
      this.areas.push(
        new AreaState({
          connectionStorage: this.connectionStorage,
          id: id,
          vertices: vertices,
          rawArea: rawArea,
          groupId: gid,
          availableBoards: availableBoards,
          displayedParallelLayoutOptions: this.displayedParallelLayoutOptions,
          edgeLabelingOptions: this.edgeLabelingOptions,
          edgeLabelingMapping: this.edgeLabelingMapping,
        })
      );
    }
  }

  createConnectionsFromDescriptors(rawAreas) {
    for (const rawArea of rawAreas) {
      const descriptor = rawArea.data.descriptor;
      if (descriptor.connections) {
        for (const conn of descriptor.connections) {
          this.connectionStorage.createFromRawDataIfNeeded(conn);
        }
      }
    }
  }

  createBreakerBoardsFromDescriptors(rawAreas) {
    for (const rawArea of rawAreas) {
      const descriptor = rawArea.data.descriptor;
      if (descriptor.type === 'breaker_board') {
        const id = descriptor.id;
        const area = this.findAreaById(descriptor.area_id);
        if (area) {
          area.breakerBoards.push(
            new BreakerBoard({ id: id, rawArea: rawArea, vertices: rawArea.tessellation.poly.points, area: area })
          );
        }
      }
    }
  }

  /**
   * Split area specified by id by the segment into two areas
   * @param  {string} areaId
   * @param  {Flatten.Segment} segment
   */
  split(areaId, segment) {
    const area = this.findAreaById(areaId);

    const areas = area.split(segment, this.idStorage);

    this.connectionStorage.removeConnectionsOf(area);
    const filtered = this.areas.filter(area => area.id !== areaId);
    this.areas = filtered;
    this.areas.push(...areas);
    return areas;
  }

  deleteBorder(areaId, index) {
    const area = this.findAreaById(areaId);
    const connections = this.connectionStorage.getConnectionsOnIndex(area, index);

    if (!connections.length) {
      throw Error('Cannot delete border with no connection');
    } else if (connections.length > 1) {
      throw Error('Cannot delete border with more connections');
    } else {
      const connection = connections[0];
      const newArea = area.deleteBorder(connection, this.idStorage);
      const filtered = this.areas.filter(area => {
        return area.id !== connection.items[0].area.id && area.id !== connection.items[1].area.id;
      });
      this.areas = filtered;
      this.areas.push(newArea);
      return newArea;
    }
  }

  placeBreakerBoards(areaId, index, plankCount) {
    const area = this.findAreaById(areaId);
    if (plankCount) {
      area.placeBreakerBoards(index, plankCount, this.idStorage);
    } else {
      area.removeBreakerBoardOnIndex(index, this.idStorage);
    }
  }

  findAreaById(id) {
    return this.areas.find(el => el.id === id);
  }

  get objects() {
    return [...this.areas, ...this.breakerBoards];
  }

  get backendObjects() {
    const objects = [];
    for (const area of this.areas) {
      objects.push(...area.getBackendObjects());
    }

    return objects;
  }

  get backendObjectsForSubmit() {
    const objects = [];
    for (const area of this.areas) {
      objects.push(...area.getBackendObjects(false));
    }

    return objects;
  }

  update(rawAreas) {
    // update areas
    for (const rawArea of rawAreas) {
      const id = rawArea.data.descriptor.id;
      const isBreakerBoard = !!(rawArea.data.descriptor.type === 'breaker_board');
      if (id && !isBreakerBoard) {
        const area = this.findAreaById(id);
        area.rawArea = rawArea;
        area.resetCurrentPlanIndex();
        area.updateBorderLabelState();
      }
    }

    // update breaker boards
    for (const rawArea of rawAreas) {
      const descriptor = rawArea.data.descriptor;
      const isBreakerBoard = descriptor.type && descriptor.type === 'breaker_board';
      if (descriptor.id && isBreakerBoard && descriptor.area_id) {
        const parentArea = this.findAreaById(descriptor.area_id);
        parentArea.findBreakerBoardById(descriptor.id).rawArea = rawArea;
      }
    }
  }

  get usedBoardLengthCounts() {
    let boardCounts = {};
    for (const areaState of this.areas) {
      const areaBoardCounts = areaState.usedBoardLengthCounts;
      boardCounts = mergeTwoLengthCounts(boardCounts, areaBoardCounts);
    }
    return boardCounts;
  }

  get planSurfaceMM2() {
    let areaM2 = 0;
    for (const areaState of this.areas) {
      areaM2 += areaState.surfaceMM2;
    }
    return areaM2;
  }

  get planBoardWidthCM() {
    // defaults to width first area
    return this.areas[0]._rawArea.data.boardwidth ? this.areas[0]._rawArea.data.boardwidth : 0;
  }

  get totalWaste() {
    let boardWasteMM2 = 0;
    let boardAreaTotalMM2 = 0;

    for (const areaState of this.areas) {
      boardAreaTotalMM2 += areaState.boardAreaMM2;
      boardWasteMM2 += areaState.boardAreaMM2 * areaState.waste;
    }

    return boardWasteMM2 / boardAreaTotalMM2;
  }
}
